import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paymentsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentsAPI.getStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Continue polling
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus(sessionId);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card border-border" data-testid="payment-result">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-bold uppercase mb-2">Processing Payment</h2>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-primary" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase mb-2 text-primary" data-testid="success-title">
                  Payment Successful!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your challenge has been activated. You can now start trading!
                </p>
                <div className="space-y-3">
                  <Button 
                    className="w-full font-heading uppercase tracking-wider glow-green"
                    onClick={() => navigate('/dashboard')}
                    data-testid="go-dashboard"
                  >
                    GO TO DASHBOARD
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/challenges')}
                  >
                    PURCHASE ANOTHER
                  </Button>
                </div>
              </>
            )}

            {(status === 'error' || status === 'timeout') && (
              <>
                <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase mb-2 text-destructive" data-testid="error-title">
                  {status === 'timeout' ? 'Verification Timeout' : 'Payment Failed'}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {status === 'timeout' 
                    ? 'We couldn\'t verify your payment. If you were charged, please contact support.'
                    : 'Something went wrong with your payment. Please try again.'}
                </p>
                <div className="space-y-3">
                  <Button 
                    className="w-full font-heading uppercase tracking-wider"
                    onClick={() => navigate('/challenges')}
                    data-testid="try-again"
                  >
                    TRY AGAIN
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/dashboard')}
                  >
                    GO TO DASHBOARD
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
